/**
 * Image Gallery Component
 * =======================
 * Display and manage product images with lightbox support.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Star, Trash2, Loader2, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useSetPrimaryImage, useDeleteImage } from '@/hooks/useProductImages'
import { getImageDisplayUrl } from '@/utils/images'
import type { ProductImage } from '@/types'

interface ImageGalleryProps {
  images: ProductImage[]
  productId: number | string
  onImagesChange?: () => void
  editable?: boolean
}

interface LightboxProps {
  imageUrl: string
  altText: string
  onClose: () => void
}

/**
 * Lightbox component for viewing images in a modal with zoom support.
 */
function Lightbox({ imageUrl, altText, onClose }: LightboxProps) {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.2 : 0.2
    setScale((prev) => Math.min(Math.max(0.5, prev + delta), 5))
  }, [])

  // Handle mouse down for drag
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  // Handle mouse move for drag
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  // Handle mouse up to stop drag
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Reset zoom and position
  const resetView = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }

  // Double click to toggle zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      resetView()
    } else {
      setScale(2)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {/* Close button - top left */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        title="Close (Esc)"
      >
        <X size={24} className="text-white" />
      </button>

      {/* Zoom controls info */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        Scroll to zoom | Double-click to toggle | {Math.round(scale * 100)}%
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className="relative max-w-[80vw] max-h-[80vh] overflow-hidden cursor-grab active:cursor-grabbing"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      >
        <img
          src={imageUrl}
          alt={altText}
          className="max-w-[80vw] max-h-[80vh] object-contain select-none transition-transform duration-100"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
          }}
          draggable={false}
        />
      </div>
    </div>
  )
}

export default function ImageGallery({
  images,
  productId,
  onImagesChange,
  editable = true,
}: ImageGalleryProps) {
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(null)
  const [lightboxImage, setLightboxImage] = useState<ProductImage | null>(null)

  const setPrimaryImage = useSetPrimaryImage()
  const deleteImage = useDeleteImage()

  const handleSetPrimary = async (imageId: number) => {
    setSettingPrimaryId(imageId)
    try {
      await setPrimaryImage.mutateAsync({ productId, imageId })
      onImagesChange?.()
    } finally {
      setSettingPrimaryId(null)
    }
  }

  const handleDelete = async (imageId: number) => {
    if (!confirm('Delete this image?')) return

    setDeletingId(imageId)
    try {
      await deleteImage.mutateAsync({ productId, imageId })
      onImagesChange?.()
    } finally {
      setDeletingId(null)
    }
  }

  // Get the full URL for an image
  const getImageUrl = (image: ProductImage) => {
    return getImageDisplayUrl(image, productId)
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        No images yet. Add one below.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {images.map((image) => {
        const isDeleting = deletingId === image.id
        const isSettingPrimary = settingPrimaryId === image.id

        return (
          <div
            key={image.id}
            className={clsx(
              'relative group rounded-lg overflow-hidden border',
              image.is_primary ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
            )}
          >
            {/* Image - clickable to open lightbox */}
            <div
              className="aspect-square bg-gray-100 cursor-pointer"
              onClick={() => setLightboxImage(image)}
            >
              <img
                src={getImageUrl(image)}
                alt={image.alt_text || 'Product image'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Show placeholder on error
                  (e.target as HTMLImageElement).src =
                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext fill="%239ca3af" x="50" y="55" text-anchor="middle" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>

            {/* Primary Badge */}
            {image.is_primary && (
              <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star size={10} fill="currentColor" />
                Primary
              </div>
            )}

            {/* Actions Overlay */}
            {editable && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {/* Set Primary Button */}
                {!image.is_primary && (
                  <button
                    type="button"
                    onClick={() => handleSetPrimary(image.id)}
                    disabled={isSettingPrimary || isDeleting}
                    className="p-2 bg-white rounded-full shadow hover:bg-gray-100 disabled:opacity-50"
                    title="Set as primary"
                  >
                    {isSettingPrimary ? (
                      <Loader2 size={16} className="animate-spin text-gray-600" />
                    ) : (
                      <Star size={16} className="text-gray-600" />
                    )}
                  </button>
                )}

                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => handleDelete(image.id)}
                  disabled={isDeleting || isSettingPrimary}
                  className="p-2 bg-white rounded-full shadow hover:bg-red-50 disabled:opacity-50"
                  title="Delete image"
                >
                  {isDeleting ? (
                    <Loader2 size={16} className="animate-spin text-red-600" />
                  ) : (
                    <Trash2 size={16} className="text-red-600" />
                  )}
                </button>
              </div>
            )}
          </div>
        )
      })}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <Lightbox
          imageUrl={getImageUrl(lightboxImage)}
          altText={lightboxImage.alt_text || 'Product image'}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  )
}
